import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { SHIFT_LOCALES } from './shiftLocales';

const STORAGE_KEY = 'mc_shift_i18n_lang';
const DEFAULT_LANG = 'zh-Hant';

/** 僅 en | zh-Hant */
export function normalizeShiftLang(raw) {
  const s = String(raw || '').trim().toLowerCase().replace(/_/g, '-');
  if (!s) return null;
  if (s === 'en' || s === 'eng' || s === 'english') return 'en';
  if (
    s === 'zh'
    || s === 'zh-hant'
    || s === 'zh-tw'
    || s === 'zh-hk'
    || s === 'chinese'
    || s === 'hant'
    || s === 'tw'
  ) return 'zh-Hant';
  return null;
}

export function shiftLangToUrlParam(lng) {
  return normalizeShiftLang(lng) === 'en' ? 'en' : 'zh';
}

function readLangFromWindow() {
  if (typeof window === 'undefined') return null;
  try {
    const sp = new URLSearchParams(window.location.search);
    return normalizeShiftLang(sp.get('lang') || sp.get('lng'));
  } catch {
    return null;
  }
}

function readStoredLang() {
  const fromUrl = readLangFromWindow();
  if (fromUrl) {
    try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch { /* ignore */ }
    return fromUrl;
  }
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    const n = normalizeShiftLang(v);
    if (n) return n;
  } catch { /* ignore */ }
  return DEFAULT_LANG;
}

function applyDomLang(lang) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang === 'en' ? 'en' : 'zh-Hant';
  document.querySelectorAll('.shift-root').forEach((el) => {
    el.setAttribute('lang', lang === 'en' ? 'en' : 'zh-Hant');
  });
}

function syncLangToUrl(lang) {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const desired = shiftLangToUrlParam(lang);
    if (url.searchParams.get('lang') === desired && !url.searchParams.has('lng')) return;
    url.searchParams.delete('lng');
    url.searchParams.set('lang', desired);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch { /* ignore */ }
}

function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{\{(\w+)\}\}/g, (_, k) => (
    vars[k] != null ? String(vars[k]) : `{{${k}}}`
  ));
}

const ShiftI18nContext = createContext(null);

export function ShiftI18nProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  const setLang = useCallback((next, opts = {}) => {
    const resolved = normalizeShiftLang(next) || DEFAULT_LANG;
    setLangState(resolved);
    try { localStorage.setItem(STORAGE_KEY, resolved); } catch { /* ignore */ }
    applyDomLang(resolved);
    if (opts.syncUrl !== false) syncLangToUrl(resolved);
  }, []);

  useEffect(() => {
    applyDomLang(lang);
    // 開啟時若網址已有 lang，維持參數；若從 localStorage 來可不強制寫入
    const fromUrl = readLangFromWindow();
    if (fromUrl) syncLangToUrl(fromUrl);
  }, [lang]);

  // 監聽手動改 query（少見，但 iframe 父層改 src 時可能整頁重載）
  useEffect(() => {
    const onPop = () => {
      const fromUrl = readLangFromWindow();
      if (fromUrl && fromUrl !== lang) setLang(fromUrl, { syncUrl: false });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [lang, setLang]);

  const t = useCallback((key, vars) => {
    const primary = SHIFT_LOCALES[lang] || SHIFT_LOCALES[DEFAULT_LANG];
    const fallback = SHIFT_LOCALES[DEFAULT_LANG];
    const raw = primary[key] ?? fallback[key] ?? key;
    return interpolate(raw, vars);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <ShiftI18nContext.Provider value={value}>
      {children}
    </ShiftI18nContext.Provider>
  );
}

export function useShiftI18n() {
  const ctx = useContext(ShiftI18nContext);
  if (!ctx) {
    throw new Error('useShiftI18n must be used within ShiftI18nProvider');
  }
  return ctx;
}
